import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listInvoices, archiveInvoice, unarchiveInvoice, deleteInvoice } from '@/lib/invoices';
import {
  Badge,
  Card,
  Input,
  Button,
  Icon,
  DropdownSelect,
  DatePicker,
  RowMenu,
  toast,
  type DropdownOption,
} from '@/components/ui';
import { en } from '@/i18n/en';
import { formatDate, formatMoney } from '@/lib/format';
import type { InvoiceStatus } from '@/types/invoice';

const STATUS_OPTIONS: DropdownOption[] = [
  { value: 'all',        label: en.dashboard.list.filter.all },
  { value: 'processing', label: en.dashboard.list.filter.processing },
  { value: 'ready',      label: en.dashboard.list.filter.ready },
  { value: 'failed',     label: en.dashboard.list.filter.failed },
  { value: 'edited',     label: en.dashboard.list.filter.edited },
  { value: 'draft',      label: 'Brouillon' },
];

interface Props {
  defaultStatus?: InvoiceStatus | 'all';
  origin?: 'uploaded' | 'created' | 'all';
  archived?: 'true' | 'false' | 'all';
  title?: string;
  emptyHint?: { title: string; lede: string; ctaTo?: string; ctaLabel?: string };
}

export function InvoiceListPage({ defaultStatus = 'all', origin, archived, title, emptyHint }: Props = {}) {
  const t = en.dashboard.list;
  const qc = useQueryClient();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [status, setStatus] = useState<InvoiceStatus | 'all'>(defaultStatus);
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const query = useQuery({
    queryKey: ['invoices', { page, pageSize, status, origin, archived, q, from, to }],
    queryFn: () => listInvoices({ page, pageSize, status, origin, archived, q, from, to }),
    placeholderData: (prev) => prev,
  });

  const move = useMutation({
    mutationFn: ({ id, to }: { id: string; to: 'archive' | 'invoices' }) =>
      to === 'archive' ? archiveInvoice(id) : unarchiveInvoice(id),
    onSuccess: (_inv, vars) => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      toast(vars.to === 'archive' ? 'Déplacée vers les Archives.' : 'Déplacée vers les Factures.', 'success');
    },
    onError: (e: unknown) => toast(e instanceof Error ? e.message : 'Échec du déplacement', 'danger'),
  });
  const del = useMutation({
    mutationFn: (id: string) => deleteInvoice(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      toast('Facture supprimée.', 'info');
    },
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="stack-lg">
      <div className="row-between">
        <h1 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.025em' }}>{title ?? t.title}</h1>
        {archived === 'true' ? (
          <Link to="/dashboard/upload" className="btn btn-primary btn-sm">
            <Icon name="upload" size={14} /> Téléverser
          </Link>
        ) : (
          <Link to="/dashboard/create" className="btn btn-primary btn-sm">
            <Icon name="plus" size={14} /> Nouvelle facture
          </Link>
        )}
      </div>

      <Card>
        <div className="filter-row">
          <Input
            placeholder={t.search}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            leading={<Icon name="search" size={14} />}
          />
          <div className="filter-status">
            <DropdownSelect
              value={status}
              onChange={(v) => setStatus(v as InvoiceStatus | 'all')}
              options={STATUS_OPTIONS}
              ariaLabel="Filtrer par statut"
            />
          </div>
          <div className="filter-date">
            <DatePicker value={from} onChange={setFrom} placeholder="Du" ariaLabel="Date de début" max={to || undefined} />
          </div>
          <div className="filter-date">
            <DatePicker value={to} onChange={setTo} placeholder="Au" ariaLabel="Date de fin" min={from || undefined} />
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setStatus('all'); setQ(''); setFrom(''); setTo(''); setPage(1); }}
          >
            Réinitialiser
          </Button>
        </div>

        {query.isLoading ? (
          <div className="empty-state">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon" aria-hidden><Icon name="invoices" size={28} /></span>
            <h3>{emptyHint?.title ?? t.empty.title}</h3>
            <p>{emptyHint?.lede ?? t.empty.lede}</p>
            <Link to={emptyHint?.ctaTo ?? '/dashboard/upload'} className="btn btn-primary btn-sm" style={{ marginTop: 12 }}>
              <Icon name={emptyHint?.ctaTo === '/dashboard/create' ? 'plus' : 'upload'} size={14} /> {emptyHint?.ctaLabel ?? 'Téléverser'}
            </Link>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="cl-table">
              <thead>
                <tr>
                  <th>{t.cols.date}</th>
                  <th>{t.cols.vendor}</th>
                  <th>{t.cols.number}</th>
                  <th className="right">{t.cols.total}</th>
                  <th>{t.cols.status}</th>
                  <th>{t.cols.actions}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((inv) => (
                  <tr key={inv.id}>
                    <td>{formatDate(inv.issueDate)}</td>
                    <td>{inv.vendor.name || <span className="muted">-</span>}</td>
                    <td className="num">{inv.invoiceNumber || '-'}</td>
                    <td className="amount right">{formatMoney(inv.total, inv.currency)}</td>
                    <td><Badge variant={inv.status} /></td>
                    <td>
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <Link
                          to={
                            // Created invoices reopen in the Create page in edit mode
                            // so users get the full theme picker + live preview;
                            // uploaded scans stay on the OCR-review detail page.
                            inv.origin === 'created'
                              ? `/dashboard/create?id=${inv.id}`
                              : `/dashboard/invoices/${inv.id}`
                          }
                          className="lnk lnk-arrow"
                        >
                          {inv.origin === 'created' ? 'Modifier' : 'Ouvrir'} <Icon name="arrow-right" size={12} />
                        </Link>
                        <RowMenu
                          label="Actions sur la ligne"
                          items={[
                            inv.archived
                              ? {
                                  label: 'Déplacer vers Factures',
                                  icon: 'arrow-right' as const,
                                  onSelect: () => move.mutate({ id: inv.id, to: 'invoices' }),
                                }
                              : {
                                  label: 'Déplacer vers Archive',
                                  icon: 'arrow-right' as const,
                                  onSelect: () => move.mutate({ id: inv.id, to: 'archive' }),
                                },
                            {
                              label: 'Supprimer',
                              icon: 'x' as const,
                              tone: 'danger' as const,
                              onSelect: () => {
                                if (window.confirm(`Supprimer la facture ${inv.invoiceNumber || inv.id} ?`)) {
                                  del.mutate(inv.id);
                                }
                              },
                            },
                          ]}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
            <div className="table-footer">
              <span>{total} facture{total > 1 ? 's' : ''}</span>
              <div className="pager">
                <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Page précédente">
                  <Icon name="chevron-left" size={14} />
                </button>
                <span style={{ padding: '0 8px' }}>{page} / {totalPages}</span>
                <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Page suivante">
                  <Icon name="chevron-right" size={14} />
                </button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
